// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, LayoutGroup } from "framer-motion";
import LanguageSwitcher from "./LanguageSwitcher";
import { createPageUrl } from "../utils/createPageUrl";
import "../styles/navbar.css";

function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { key: "home", path: createPageUrl("Home") },
    { key: "tennis", path: createPageUrl("TennisCoaching"), color: "#4CAF50" },
    { key: "webDev", path: createPageUrl("WebDevelopment"), color: "#2D8CFF" },
    { key: "portfolio", path: createPageUrl("Portfolio"), color: "#2D8CFF" }
  ];

  return (
    <header className={"navbar" + (isScrolled ? " navbar--scrolled" : "")}>
      <nav className="navbar__inner">
        {/* Logo / Brand */}
        <NavLink to={createPageUrl("Home")} className="navbar__brand">
          <div className="navbar__logo">
            <img
              src="/logo.png"
              alt="Serve & Code logo"
              className="navbar__logo-img"
            />
          </div>
          <div className="navbar__brand-text">
            <span className="navbar__brand-title">
              Serve <span className="navbar__brand-highlight">&</span> Code
            </span>
            <span className="navbar__brand-subtitle">Tennis & Tech</span>
          </div>
        </NavLink>

        {/* Desktop nav + language */}
        <div className="navbar__links-desktop">
          <LayoutGroup id="navbar-links">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  className="navbar__link"
                  style={
                    isActive && item.color
                      ? { "--navbar-active-color": item.color }
                      : {}
                  }
                >
                  {({ isActive: navIsActive }) => (
                    <div className="navbar__link-inner">
                      <span className="navbar__link-label">
                        {t(`nav.${item.key}`)}
                      </span>
                      {navIsActive && (
                        <motion.span
                          layoutId="navbar-active-underline"
                          className="navbar__link-underline"
                          style={
                            item.color
                              ? { backgroundColor: item.color }
                              : undefined
                          }
                          transition={{
                            type: "spring",
                            stiffness: 280,
                            damping: 30
                          }}
                        />
                      )}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </LayoutGroup>
          <LanguageSwitcher />
        </div>

        {/* Mobile controls */}
        <div className="navbar__mobile-controls">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="navbar__menu-button"
          >
            {isMenuOpen ? (
              <X className="navbar__menu-icon" />
            ) : (
              <Menu className="navbar__menu-icon" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="navbar__mobile-menu">
          <div className="navbar__mobile-menu-inner">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  className={
                    "navbar__mobile-link" +
                    (isActive ? " navbar__mobile-link--active" : "")
                  }
                  style={
                    isActive && item.color
                      ? { "--navbar-active-color": item.color }
                      : {}
                  }
                >
                  {t(`nav.${item.key}`)}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
